import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./SendersDurationChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardLegendTopNoClick,
    buildMonitorDashboardResponsiveChartOptions,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class SendersDurationChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;

    private chart: any = null;
    private canvasId: string = "senders-duration-chart-canvas";

    public hasData: boolean = true;
    public isLoading: boolean = false;
    private requestVersion: number = 0;
    private barCounts: number[] = [];

    public beforeDestroy(): void {
        this.destroyChart();
    }

    public onTitleClick(): void {
        this.navigateToDetails(null);
    }

    private navigateToDetails(barIndex: number | null): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        const query: any = {};
        if (barIndex != null && barIndex >= 0 && barIndex < 5) {
            query.duration = String(barIndex + 1);
        }

        this.$router.push({
            name: "SendersDurationDetails",
            query: query,
        });
    }

    private onChartClick(evt: any, elements: any[]): void {
        if (!elements || !elements.length) {
            return;
        }

        const index = elements[0]._index != null ? Number(elements[0]._index) : -1;
        if (index < 0 || index >= 5) {
            return;
        }

        const count = this.barCounts.length > index ? Number(this.barCounts[index]) : 0;
        if (!count || count <= 0) {
            return;
        }

        this.navigateToDetails(index);
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const resp = await this.requestsSendersService.GetSendersDuration(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.destroyChart();
            return;
        }

        const rows =
            resp && resp.result && Array.isArray(resp.result) ? resp.result : [];

        const countsByDuration: any = {};
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r) continue;

            const d = r.duration != null ? Number(r.duration) : 0;
            const c = r.count != null ? Number(r.count) : 0;
            if (d > 0) {
                countsByDuration[d] = c;
            }
        }

        const bars: { label: string; count: number }[] = [];
        for (let d = 1; d <= 5; d++) {
            const count = countsByDuration.hasOwnProperty(d) ? Number(countsByDuration[d]) : 0;
            bars.push({
                label: d === 5 ? "5+" : String(d),
                count: count,
            });
        }

        const total = bars.reduce(function (s, x) { return s + x.count; }, 0);
        this.hasData = total > 0;
        this.barCounts = bars.map(function (b) { return b.count; });

        await this.$nextTick();
        this.renderChart(bars);
    }

    private renderChart(bars: { label: string; count: number }[]): void {
        this.destroyChart();

        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;

        const labels = ["1 Year", "2 Years", "3 Years", "4 Years", "5+ Years"];
        const values = bars.map(function (b) { return b.count; });
        const yMax = this.computeNiceYAxisMax(values);
        const self = this;

        const datasets: any[] = [];
        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();

        for (let i = 0; i < labels.length; i++) {
            const fill = getMonitorDashboardChartColor("sendersDuration", String(i + 1));
            datasets.push({
                label: labels[i],
                data: [i === 0 ? values[0] : 0, i === 1 ? values[1] : 0, i === 2 ? values[2] : 0, i === 3 ? values[3] : 0, i === 4 ? values[4] : 0],
                backgroundColor: fill,
                borderColor: monitorDashboardDarkerBorder(fill),
                borderWidth: 2,
                categoryPercentage: 0.7,
                barPercentage: 0.85,
            });
        }

        this.chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: labels,
                datasets: datasets,
            },
            plugins: [
                createMonitorDashboardLegendHeightPlugin(14),
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawTopValueLabels(chart);
                    },
                },
            ],
            options: Object.assign({}, baseOpts, {
                legend: buildMonitorDashboardLegendTopNoClick(fontFamily),
                onClick: function (evt: any, elements: any[]) {
                    self.onChartClick(evt, elements);
                },
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            gridLines: { display: false },
                            ticks: {
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                        },
                    ],
                    yAxes: [{
                        stacked: true,
                        ticks: {
                            beginAtZero: true,
                            precision: 0,
                            max: yMax,
                            fontColor: "#6b7280",
                            fontFamily: fontFamily,
                        },
                    }],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            return String(items[0].xLabel || "");
                        },
                        label: function (item: any) {
                            const raw = item && item.yLabel != null ? Number(item.yLabel) : 0;
                            const value = isNaN(raw) ? 0 : raw;
                            return String(self.$t("shared.senders")) + ": " + String(value);
                        },
                    },
                }),
            }),
        });
    }

    private drawTopValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "bottom";
        ctx2.font = "10px " + getMonitorDashboardFontFamily();
        ctx2.fillStyle = "#6b7280";

        const labelsCount = chart.data && chart.data.labels ? chart.data.labels.length : 0;
        if (labelsCount === 0) {
            ctx2.restore();
            return;
        }

        const xMeta = chart.getDatasetMeta(0);
        if (!xMeta || !xMeta.data || xMeta.data.length === 0) {
            ctx2.restore();
            return;
        }

        const yAxis = chart.scales["y-axis-0"];
        const yZero = yAxis ? yAxis.getPixelForValue(0) : 0;

        for (let i = 0; i < labelsCount; i++) {
            let total = 0;
            let yTop = Number.MAX_VALUE;
            for (let di = 0; di < chart.data.datasets.length; di++) {
                const meta = chart.getDatasetMeta(di);
                if (meta && meta.hidden) continue;
                const v = Number(chart.data.datasets[di].data[i] || 0);
                total += v;
                if (v > 0 && meta && meta.data && meta.data[i] && meta.data[i]._model) {
                    const y = Number(meta.data[i]._model.y);
                    if (y < yTop) yTop = y;
                }
            }

            const x = xMeta.data[i]._model.x;
            if (total === 0) {
                ctx2.fillText("0", x, yZero - 6);
            } else {
                const y = (yTop === Number.MAX_VALUE ? yZero : yTop) - 6;
                ctx2.fillText(String(total), x, y);
            }
        }

        ctx2.restore();
    }

    private drawZeroValueMarkers(chart: any): void {
        const ctx2: any = chart.ctx;
        const yAxis = chart.scales["y-axis-0"];
        if (!yAxis) return;

        const yZero = yAxis.getPixelForValue(0);
        ctx2.save();
        ctx2.lineWidth = 3;

        for (let di = 0; di < chart.data.datasets.length; di++) {
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.hidden) continue;

            const strokeColor = chart.data.datasets[di].borderColor;

            for (let i = 0; i < meta.data.length; i++) {
                const val = Number(chart.data.datasets[di].data[i]);
                if (val !== 0) continue;

                const bar = meta.data[i];
                const model = bar._model;
                const x = model.x;
                const halfWidth = Math.max(1, (model.width || 0) / 2);

                ctx2.strokeStyle = strokeColor;
                ctx2.beginPath();
                ctx2.moveTo(x - halfWidth, yZero);
                ctx2.lineTo(x + halfWidth, yZero);
                ctx2.stroke();
            }
        }

        ctx2.restore();
    }

    private computeNiceYAxisMax(values: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < values.length; i++) {
            const v = Number(values[i]);
            if (v > maxVal) maxVal = v;
        }

        if (maxVal <= 0) return 10;

        const rounded = Math.ceil(maxVal / 100) * 100;
        return rounded < 10 ? 10 : rounded;
    }
}
